import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight } from 'lucide-react';
import CommonLayout from '../layout/CommonLayout';
import Spinner from '../components/Common/Spinner';
import MultiProjectFiltersRow from '../components/MultiProject/MultiProjectFiltersRow';
import MultiProjectIssuesTable from '../components/MultiProject/MultiProjectIssuesTable';
import StandUpPRSummarySection from '../components/StandUp/StandUpPRSummarySection';
import { PRColumns } from '../components/AGColumns/PRsColumn';
import { buildStandupPRSummaryTooltip } from '../utils/firstPassPrUtils';
import {
  setMultiProjectCompanyId,
  setMultiProjectFilters,
  setMultiProjectSelections,
  setMultiProjectFilterMode,
  setMultiProjectIssuesData,
  setMultiProjectLoading,
  setMultiProjectError,
  hydrateMultiProjectFromSession,
} from '../store/MultiProjectSlice/multiProjectSlice';
import {
  getMultiProjectIssues,
  getStandupDashboardData,
  getId,
  APP_STRINGS,
} from '../constants';
import {
  toIdString,
  getProjectById,
  getReposForProjects,
  getSprintsForProjects,
  getReleasesForProjects,
  buildMultiProjectIssuesPayload,
  resolveDefaultMultiProjectSelections,
  computeStatusBadgeData,
  filterIssuesByStatus,
  filterPRsByRepos,
  shouldShowMultiProjectPRTable,
  mapJiraApiIssueToStandupRow,
  flattenIssuesToPullRequests,
  mapMultiProjectPrToGridRow,
  filterPrGridRowsByType,
  aggregateMultiProjectOpenPrData,
  normalizeSelectedProjects,
  getSelectedProjectIds,
  getProjectDocsFromSelections,
  scopeMultiProjectFiltersByAccessibleProjects,
} from '../utils/multiProjectUtils';

const AGrid = lazy(() => import('../components/Common/AGgrid'));

function MultiProjectSelectionPage() {
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme.theme);
  const accessibleProjectList = useSelector((state) => state.jira?.projectList || []);
  const {
    filters,
    selectedProjects,
    selectedSprintIds,
    selectedReleaseIds,
    selectedRepos,
    filterMode,
    issuesData,
    loading,
    error,
  } = useSelector((state) => state.multiProject);

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [prFilterType, setPrFilterType] = useState('all');
  const [initialized, setInitialized] = useState(false);
  const fetchRequestIdRef = useRef(0);
  const prGridApiRef = useRef(null);

  const companyId = getId().companyId;

  const scopeFilters = useCallback(
    (filterData) => scopeMultiProjectFiltersByAccessibleProjects(filterData, accessibleProjectList),
    [accessibleProjectList],
  );

  const ensureFilters = useCallback(async () => {
    if (filters?.projects?.length) return scopeFilters(filters);
    try {
      const typeValue = sessionStorage.getItem(APP_STRINGS.SESSION_TYPE_VALUE);
      const value =
        typeValue === APP_STRINGS.VALUE_RELEASE
          ? APP_STRINGS.API_RELEASE
          : APP_STRINGS.API_SPRINT;
      const response = await getStandupDashboardData({
        value,
        sections: 'getmultiProjectfilters',
      });
      const filterData = scopeFilters(response?.data?.getmultiProjectfilters);
      if (filterData) {
        dispatch(setMultiProjectFilters(filterData));
        if (companyId) dispatch(setMultiProjectCompanyId(companyId));
        return filterData;
      }
    } catch (err) {
      console.error('Failed to load multi-project filters:', err);
    }
    return null;
  }, [companyId, dispatch, filters, scopeFilters]);

  useEffect(() => {
    dispatch(hydrateMultiProjectFromSession());
    if (companyId) {
      dispatch(setMultiProjectCompanyId(companyId));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const filterData = filters?.projects?.length ? filters : await ensureFilters();
      if (cancelled || !filterData?.projects?.length) {
        setInitialized(true);
        return;
      }

      const defaults = resolveDefaultMultiProjectSelections({
        filters: filterData,
        persistedProjects: selectedProjects,
        persistedSprintIds: selectedSprintIds,
        persistedReleaseIds: selectedReleaseIds,
        persistedRepos: selectedRepos,
        persistedFilterMode: filterMode,
      });

      const needsPersist =
        JSON.stringify(defaults.selectedProjects) !== JSON.stringify(selectedProjects) ||
        JSON.stringify(defaults.selectedSprintIds) !== JSON.stringify(selectedSprintIds) ||
        JSON.stringify(defaults.selectedReleaseIds) !== JSON.stringify(selectedReleaseIds) ||
        JSON.stringify(defaults.selectedRepos) !== JSON.stringify(selectedRepos) ||
        defaults.filterMode !== filterMode;

      if (needsPersist) {
        dispatch(
          setMultiProjectSelections({
            selectedProjects: defaults.selectedProjects,
            selectedSprintIds: defaults.selectedSprintIds,
            selectedReleaseIds: defaults.selectedReleaseIds,
            selectedRepos: defaults.selectedRepos,
            filterMode: defaults.filterMode,
          }),
        );
      }

      setInitialized(true);
    };

    if (!initialized) {
      bootstrap();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, initialized]);

  const fetchIssues = useCallback(async () => {
    const selectedProjectIds = getSelectedProjectIds(selectedProjects);
    const effectiveFilters = scopeFilters(filters);
    if (!selectedProjectIds.length || !effectiveFilters) return;

    const requestId = ++fetchRequestIdRef.current;
    dispatch(setMultiProjectLoading(true));
    dispatch(setMultiProjectError(null));

    try {
      const payload = buildMultiProjectIssuesPayload({
        selectedProjects,
        selectedSprintIds,
        selectedReleaseIds,
        filterMode,
        filters: effectiveFilters,
      });

      const response = await getMultiProjectIssues({
        companyId,
        ...payload,
      });

      if (requestId !== fetchRequestIdRef.current) return;

      dispatch(setMultiProjectIssuesData(response?.data ?? null));
      setSelectedStatuses([]);
      setPrFilterType('all');
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Failed to fetch multi-project issues:', err);
      dispatch(setMultiProjectError(err?.response?.data?.error || err.message || 'Request failed'));
      dispatch(setMultiProjectIssuesData(null));
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        dispatch(setMultiProjectLoading(false));
      }
    }
  }, [
    companyId,
    dispatch,
    filters,
    filterMode,
    scopeFilters,
    selectedProjects,
    selectedSprintIds,
    selectedReleaseIds,
  ]);

  useEffect(() => {
    if (!initialized || !getSelectedProjectIds(selectedProjects).length) return;
    fetchIssues();
  }, [initialized, fetchIssues, selectedProjects]);

  const scopedFilters = useMemo(
    () => scopeFilters(filters),
    [filters, scopeFilters],
  );

  const projectOptions = useMemo(() => {
    const normalized = normalizeSelectedProjects(selectedProjects, scopedFilters);
    return (scopedFilters?.projects || []).map((project) => {
      const projectId = toIdString(project._id);
      const selection = normalized.find((entry) => entry.projectId === projectId);
      const board = selection?.boardId
        ? (project.boards || []).find((b) => toIdString(b._id) === selection.boardId)
        : null;
      const baseLabel = project.name || project.key || projectId;
      const label =
        board && (project.boards?.length || 0) > 1
          ? `${baseLabel} - ${board.boardName}`
          : baseLabel;

      return {
        label,
        value: projectId,
        boardCount: project.boards?.length || 0,
        hasMultipleBoards: (project.boards?.length || 0) > 1,
        selectedBoardId: selection?.boardId || null,
      };
    });
  }, [scopedFilters, selectedProjects]);

  const selectedProjectDocs = useMemo(
    () => getProjectDocsFromSelections(scopedFilters, selectedProjects),
    [scopedFilters, selectedProjects],
  );

  const renderProjectOptionLabel = useCallback((option) => (
    <div className="flex items-center justify-between w-full pr-2">
      <span>{option.label}</span>
      {option.hasMultipleBoards && <ChevronRight className="w-4 h-4 text-gray-400" />}
    </div>
  ), []);

  const getProjectBoards = useCallback(
    (projectId) => getProjectById(scopedFilters, projectId)?.boards || [],
    [scopedFilters],
  );

  const repoOptions = useMemo(
    () =>
      getReposForProjects(selectedProjectDocs).map((repo) => ({
        label: repo.split('/').pop() || repo,
        value: repo,
      })),
    [selectedProjectDocs],
  );

  const sprintOptions = useMemo(
    () => getSprintsForProjects(scopedFilters, getSelectedProjectIds(selectedProjects)),
    [scopedFilters, selectedProjects],
  );

  const releaseOptions = useMemo(
    () => getReleasesForProjects(scopedFilters, getSelectedProjectIds(selectedProjects)),
    [scopedFilters, selectedProjects],
  );

  const handleFilterModeChange = (mode) => {
    dispatch(
      setMultiProjectSelections({
        selectedSprintIds: mode === 'backlog' || mode === 'release' ? [] : selectedSprintIds,
        selectedReleaseIds: mode === 'backlog' || mode === 'sprint' ? [] : selectedReleaseIds,
        filterMode: mode,
      }),
    );
    dispatch(setMultiProjectFilterMode(mode));
    setSelectedStatuses([]);
  };

  const handleProjectsChange = (values) => {
    const projectIds = values.map(toIdString);
    const normalized = normalizeSelectedProjects(selectedProjects, scopedFilters);
    const nextSelections = projectIds.map((projectId) => {
      const existing = normalized.find((entry) => entry.projectId === projectId);
      if (existing) return existing;
      const project = getProjectById(scopedFilters, projectId);
      return {
        projectId,
        boardId: toIdString(project?.defaultBoardId) || null,
      };
    });
    const projects = getProjectDocsFromSelections(scopedFilters, nextSelections);
    const repos = getReposForProjects(projects);
    const validSprintIds = getSprintsForProjects(scopedFilters, projectIds).map((s) => toIdString(s._id));
    const validReleaseIds = getReleasesForProjects(scopedFilters, projectIds).map((r) =>
      toIdString(r._id),
    );

    dispatch(
      setMultiProjectSelections({
        selectedProjects: nextSelections,
        selectedSprintIds: selectedSprintIds.filter((id) => validSprintIds.includes(id)),
        selectedReleaseIds: selectedReleaseIds.filter((id) => validReleaseIds.includes(id)),
        selectedRepos: repos,
      }),
    );
    setSelectedStatuses([]);
  };

  const handleBoardSelect = (boardId, projectId) => {
    const projectIdStr = toIdString(projectId);
    const boardIdStr = toIdString(boardId);
    const normalized = normalizeSelectedProjects(selectedProjects, scopedFilters);
    const existingIndex = normalized.findIndex((entry) => entry.projectId === projectIdStr);
    const nextEntry = { projectId: projectIdStr, boardId: boardIdStr };

    const nextSelections =
      existingIndex >= 0
        ? normalized.map((entry, index) => (index === existingIndex ? nextEntry : entry))
        : [...normalized, nextEntry];

    const projects = getProjectDocsFromSelections(scopedFilters, nextSelections);
    const repos = getReposForProjects(projects);
    const projectIds = getSelectedProjectIds(nextSelections);
    const validSprintIds = getSprintsForProjects(scopedFilters, projectIds).map((s) => toIdString(s._id));
    const validReleaseIds = getReleasesForProjects(scopedFilters, projectIds).map((r) =>
      toIdString(r._id),
    );

    dispatch(
      setMultiProjectSelections({
        selectedProjects: nextSelections,
        selectedSprintIds: selectedSprintIds.filter((id) => validSprintIds.includes(id)),
        selectedReleaseIds: selectedReleaseIds.filter((id) => validReleaseIds.includes(id)),
        selectedRepos: repos,
      }),
    );
    setSelectedStatuses([]);
  };

  const handleReposChange = (values) => {
    dispatch(setMultiProjectSelections({ selectedRepos: values }));
  };

  const handleSprintsChange = (values) => {
    const sprintIds = values.map(toIdString);
    dispatch(
      setMultiProjectSelections({
        selectedSprintIds: sprintIds,
        selectedReleaseIds: sprintIds.length ? [] : selectedReleaseIds,
        filterMode: sprintIds.length ? 'sprint' : filterMode === 'sprint' ? null : filterMode,
      }),
    );
    if (sprintIds.length) {
      dispatch(setMultiProjectFilterMode('sprint'));
    }
    setSelectedStatuses([]);
  };

  const handleReleasesChange = (values) => {
    const releaseIds = values.map(toIdString);
    dispatch(
      setMultiProjectSelections({
        selectedReleaseIds: releaseIds,
        selectedSprintIds: releaseIds.length ? [] : selectedSprintIds,
        filterMode: releaseIds.length ? 'release' : filterMode === 'release' ? null : filterMode,
      }),
    );
    if (releaseIds.length) {
      dispatch(setMultiProjectFilterMode('release'));
    }
    setSelectedStatuses([]);
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const rawIssues = useMemo(() => issuesData?.issues || [], [issuesData?.issues]);

  const statusBadgeData = useMemo(
    () => computeStatusBadgeData(rawIssues),
    [rawIssues],
  );

  const filteredRowData = useMemo(() => {
    const statusFiltered = filterIssuesByStatus(rawIssues, selectedStatuses);
    return statusFiltered.map((issue) => mapJiraApiIssueToStandupRow(issue)).filter(Boolean);
  }, [rawIssues, selectedStatuses]);

  const allRepos = useMemo(() => getReposForProjects(selectedProjectDocs), [selectedProjectDocs]);

  const showPRTable = shouldShowMultiProjectPRTable(selectedSprintIds, selectedReleaseIds, filterMode) && allRepos.length > 0;
  const prsData = useMemo(() => {
    const raw = issuesData?.PRsData || [];
    return filterPRsByRepos(raw, selectedRepos);
  }, [issuesData?.PRsData, selectedRepos]);

  const openPrData = useMemo(
    () => aggregateMultiProjectOpenPrData(prsData, selectedRepos, allRepos),
    [prsData, selectedRepos, allRepos],
  );

  const sourceManagement = issuesData?.sourceManagement;
  const isGitLab = String(sourceManagement || '').toLowerCase() === 'gitlab';
  const prLabel = isGitLab ? 'Merge Requests' : 'Pull Requests';
  const prShortLabel = isGitLab ? 'MR' : 'PR';
  const prPluralShortLabel = `${prShortLabel}s`;
  const prSummaryTooltip = buildStandupPRSummaryTooltip({
    prLabel,
    prPluralShortLabel,
    prShortLabel,
  });

  const filteredPRColumns = useMemo(() => PRColumns(sourceManagement, false), [sourceManagement]);

  const prRowData = useMemo(() => {
    if (!selectedRepos.length) return [];

    const repoSet =
      selectedRepos.length >= allRepos.length
        ? null
        : new Set(
            selectedRepos.map((r) => {
              const shortRepo = r.split('/').pop() || r;
              return shortRepo.toLowerCase();
            })
          );

    const flatPrs = flattenIssuesToPullRequests(rawIssues).filter((pr) => {
      if (!repoSet) return true;
      return repoSet.has(String(pr?.repo || '').toLowerCase());
    });

    return filterPrGridRowsByType(
      flatPrs.map((pr) => mapMultiProjectPrToGridRow(pr)).filter(Boolean),
      prFilterType,
    );
  }, [rawIssues, selectedRepos, allRepos.length, prFilterType]);

  const onPRGridReady = useCallback((api) => {
    prGridApiRef.current = api;
  }, []);

  const chartSuspenseFallback = <Spinner />;

  const noFilters = !filters?.projects?.length;

  return (
    <div className="w-screen">
      <CommonLayout>
        {() => (
          <div className="mt-20 ml-0 pb-10 px-5">
            {noFilters && !loading ? (
              <div className="text-[#24527A] dark:text-[#D9E4F1]">
                Filter data is not available yet. Please visit the Standup page first or refresh
                after your project loads.
              </div>
            ) : (
              <>
                <MultiProjectFiltersRow
                  projectOptions={projectOptions}
                  repoOptions={repoOptions}
                  sprintOptions={sprintOptions}
                  releaseOptions={releaseOptions}
                  selectedProjects={selectedProjects}
                  selectedRepos={selectedRepos}
                  selectedSprintIds={selectedSprintIds}
                  selectedReleaseIds={selectedReleaseIds}
                  filterMode={filterMode}
                  onProjectsChange={handleProjectsChange}
                  onBoardSelect={handleBoardSelect}
                  getProjectBoards={getProjectBoards}
                  onReposChange={handleReposChange}
                  onSprintsChange={handleSprintsChange}
                  onReleasesChange={handleReleasesChange}
                  onFilterModeChange={handleFilterModeChange}
                  loading={loading}
                  renderProjectOptionLabel={renderProjectOptionLabel}
                />

                {error && (
                  <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                    {error}
                  </div>
                )}

                {showPRTable && (
                  <Suspense fallback={<Spinner />}>
                    <div className="mb-4">
                      <StandUpPRSummarySection
                        theme={theme}
                        prLabel={prLabel}
                        prPluralShortLabel={prPluralShortLabel}
                        prShortLabel={prShortLabel}
                        prSummaryTooltip={prSummaryTooltip}
                        openPrData={openPrData}
                        prFilterType={prFilterType}
                        setPrFilterType={setPrFilterType}
                        heavyChartsReady={!loading}
                        chartSuspenseFallback={chartSuspenseFallback}
                        AGrid={AGrid}
                        prRowData={prRowData}
                        filteredPRColumns={filteredPRColumns}
                        onPRGridReady={onPRGridReady}
                        prGridColumnModeKey="multi-project-pr-grid"
                      />
                    </div>
                  </Suspense>
                )}

                <Suspense fallback={<Spinner />}>
                  <MultiProjectIssuesTable
                    theme={theme}
                    loading={loading}
                    rowData={filteredRowData}
                    sourceManagement={sourceManagement}
                    AGrid={AGrid}
                    statusBadgeData={statusBadgeData}
                    selectedStatuses={selectedStatuses}
                    onToggleStatus={toggleStatus}
                    isBacklogMode={filterMode === 'backlog'}
                  />
                </Suspense>
              </>
            )}
          </div>
        )}
      </CommonLayout>
    </div>
  );
}

export default MultiProjectSelectionPage;
